/* eslint-disable jsdoc/require-jsdoc */
/* eslint-disable import/no-extraneous-dependencies */
import { ReactElement, createContext, useContext, useMemo, useRef, useState } from 'react';
import { Line, Bar, Doughnut, Scatter } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  ArcElement,
  Tooltip,
  Filler,
  TimeSeriesScale
} from 'chart.js';
import 'chartjs-adapter-moment';
import {
  TrendingUp,
  TrendingDown,
  Minus,
  AlertTriangle,
  CheckCircle2,
  Info,
  Download
} from 'lucide-react';
import { MetricCard, RenderBlock } from '../../services/ai-agent.service';
import { pinnedTooltipsPlugin } from '../../chartPlugins';
import {
  ClearPinsButton,
  usePinnedTooltips,
  withPinnedTooltips
} from '../../hooks/usePinnedTooltips';

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  ArcElement,
  Filler,
  Tooltip,
  TimeSeriesScale
);

/**
 * User-tunable chart appearance, shared across every chart on the page.
 * All inputs go through {@link clampChartSettings} before being applied so the
 * user can't drive the charts into a broken state (size 0, invalid colour, …).
 */
export interface ChartSettings {
  /** Base font size in px (axis ticks, legend, tooltip). */
  fontSize: number;
  /** Hex colour used for axis ticks, legend labels, and SVG text. */
  tickColor: string;
  /** Hex colour used (at 15% alpha) for axis grid lines. */
  gridColor: string;
  /**
   * Optional hex colour applied to ALL data series (line strokes, bar fill,
   * scatter points, box-plot strokes). When set, overrides each series' own
   * palette colour. Leave blank/empty to let each parameter use its native
   * palette colour. Donut and gauge keep their semantic palettes either way.
   */
  seriesColor: string;
  showGrid: boolean;
  showLegend: boolean;
}

export const DEFAULT_CHART_SETTINGS: ChartSettings = {
  fontSize: 13,
  // White text for ticks, labels, legend, tooltip — kept consistent across
  // every chart. Grid lines also default to white but are drawn at 15% alpha
  // by `withSettings`, so they read as faint guides rather than solid white.
  tickColor: '#FFFFFF',
  gridColor: '#FFFFFF',
  seriesColor: '',
  showGrid: true,
  showLegend: true
};

const HEX_RE = /^#([0-9a-fA-F]{3}|[0-9a-fA-F]{6})$/;

/**
 * Coerce any user-supplied settings into a guaranteed-valid object. Out-of-range
 * font sizes are clamped (10–28), invalid hex colours fall back to defaults,
 * and the booleans are normalised. The chart layer can rely on the result.
 * @param s - Partial / possibly-untrusted settings.
 * @returns A fully-populated, validated settings object.
 */
export function clampChartSettings(s: Partial<ChartSettings> | undefined): ChartSettings {
  const fs = Number(s?.fontSize);
  // seriesColor: empty string is a valid "no override"; only fill in a real
  // hex if it parses, otherwise keep the previous/empty value.
  let series: string;
  if (typeof s?.seriesColor === 'string') {
    series = s.seriesColor === '' || HEX_RE.test(s.seriesColor) ? s.seriesColor : '';
  } else {
    series = DEFAULT_CHART_SETTINGS.seriesColor;
  }
  return {
    fontSize: Math.max(10, Math.min(28, Number.isFinite(fs) ? Math.round(fs) : DEFAULT_CHART_SETTINGS.fontSize)),
    tickColor:
      typeof s?.tickColor === 'string' && HEX_RE.test(s.tickColor)
        ? s.tickColor
        : DEFAULT_CHART_SETTINGS.tickColor,
    gridColor:
      typeof s?.gridColor === 'string' && HEX_RE.test(s.gridColor)
        ? s.gridColor
        : DEFAULT_CHART_SETTINGS.gridColor,
    seriesColor: series,
    showGrid: s?.showGrid !== false,
    showLegend: s?.showLegend !== false
  };
}

const ChartSettingsContext = createContext<ChartSettings>(DEFAULT_CHART_SETTINGS);
export const ChartSettingsProvider = ChartSettingsContext.Provider;

/**
 * Hook used by every chart renderer to pull the current global settings.
 * @returns The active {@link ChartSettings}.
 */
function useChartSettings(): ChartSettings {
  return useContext(ChartSettingsContext);
}

/**
 * Convert a 6-digit hex to an `rgba()` string at the given alpha. Falls back
 * to the default tick-grey if the input isn't a valid #rrggbb.
 * @param hex - A `#rrggbb` colour string.
 * @param alpha - Opacity in [0, 1].
 * @returns The CSS `rgba()` string.
 */
function hexToRgba(hex: string, alpha: number): string {
  const m = /^#([0-9a-fA-F]{6})$/.exec(hex);
  if (!m) return `rgba(75,85,99,${alpha})`;
  const r = parseInt(m[1].slice(0, 2), 16);
  const g = parseInt(m[1].slice(2, 4), 16);
  const b = parseInt(m[1].slice(4, 6), 16);
  return `rgba(${r},${g},${b},${alpha})`;
}

/**
 * Layer the user's chart settings onto a Chart.js options object built by a
 * specific chart component. Mutates and returns the options for convenience;
 * existing callbacks (tick formatters, tooltip labels) are preserved.
 * @param options - The chart's base options.
 * @param s - Validated chart settings.
 * @returns The same options, with appearance settings applied.
 */
// eslint-disable-next-line no-param-reassign
function withSettings(options: any, s: ChartSettings): any {
  /* eslint-disable no-param-reassign */
  options.plugins = options.plugins || {};

  // Legend.
  const legend = options.plugins.legend || {};
  const baseLegendDisplay = legend.display === undefined ? true : legend.display;
  options.plugins.legend = {
    ...legend,
    display: baseLegendDisplay && s.showLegend,
    labels: {
      ...(legend.labels || {}),
      color: s.tickColor,
      font: { ...(legend.labels?.font || {}), size: s.fontSize }
    }
  };

  // Tooltip — title/body color follow the tick color so the whole chart sits
  // in one palette (no stray grey text anywhere).
  const tooltip = options.plugins.tooltip || {};
  options.plugins.tooltip = {
    ...tooltip,
    titleColor: s.tickColor,
    bodyColor: s.tickColor,
    titleFont: { ...(tooltip.titleFont || {}), size: s.fontSize },
    bodyFont: { ...(tooltip.bodyFont || {}), size: s.fontSize },
    padding: tooltip.padding ?? 10
  };

  // Scales (cartesian charts only).
  if (options.scales) {
    Object.keys(options.scales).forEach((k) => {
      const ax = options.scales[k];
      if (!ax) return;
      ax.ticks = {
        ...(ax.ticks || {}),
        color: s.tickColor,
        font: { ...(ax.ticks?.font || {}), size: s.fontSize }
      };
      const baseGridDisplay = ax.grid?.display === undefined ? true : ax.grid.display;
      ax.grid = {
        ...(ax.grid || {}),
        display: baseGridDisplay && s.showGrid,
        color: hexToRgba(s.gridColor, 0.15)
      };
      if (ax.title) {
        ax.title = {
          ...ax.title,
          color: s.tickColor,
          font: { ...(ax.title.font || {}), size: Math.max(10, s.fontSize - 1) }
        };
      }
    });
  }

  return options;
  /* eslint-enable no-param-reassign */
}

/** Named export backgrounds. `null` means transparent (PNG only). */
const EXPORT_BACKGROUNDS: Record<string, string | null> = {
  dark: '#0F172A',
  light: '#FFFFFF',
  transparent: null
};

/** User choices for a chart image export, picked in the download popover. */
export interface ExportOptions {
  /** Backdrop painted under the chart. */
  background: 'dark' | 'light' | 'transparent';
  /** Output resolution multiplier (1×–3×). */
  scale: number;
  /** Image encoding. */
  format: 'png' | 'jpeg';
}

const DEFAULT_EXPORT: ExportOptions = { background: 'dark', scale: 2, format: 'png' };

/**
 * Resolve the fill colour to paint under an export. JPEG has no alpha channel,
 * so a "transparent" request falls back to white to avoid a black box.
 * @param opts - The chosen export options.
 * @returns A hex fill colour, or null to leave the surface transparent.
 */
function exportFill(opts: ExportOptions): string | null {
  const bg = EXPORT_BACKGROUNDS[opts.background];
  if (bg) return bg;
  return opts.format === 'jpeg' ? '#FFFFFF' : null;
}

/**
 * Convert a chart title into a safe download filename stem.
 * @param stem - The chart's user-visible title.
 * @returns A lowercased, underscore-separated, alphanumeric-only file stem.
 */
function safeFilename(stem: string): string {
  const cleaned = stem
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '');
  return cleaned || 'chart';
}

/**
 * Push a rendered blob to the browser as a file download.
 * @param blob - The image blob.
 * @param title - The chart's user-visible title; turned into a file name.
 * @param format - The image encoding, used to pick the extension.
 */
function triggerDownload(blob: Blob, title: string, format: 'png' | 'jpeg'): void {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `${safeFilename(title)}.${format === 'jpeg' ? 'jpg' : 'png'}`;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

/** Dark ink + gridlines used when exporting onto a light/transparent surface. */
const EXPORT_LIGHT_INK = '#374151';
const EXPORT_LIGHT_GRID = 'rgba(55,65,81,0.25)';

/**
 * True for the theme's white text/grid colours, which vanish on a light export.
 * @param c - A colour value (hex string or CSS name).
 * @returns Whether the colour reads as white.
 */
function isWhiteish(c: unknown): boolean {
  const s = String(c).toLowerCase();
  return s === '#fff' || s === '#ffffff' || s === 'white';
}

/**
 * The chart is dark-themed (white gridlines + white default text), which
 * disappears when exported onto a light or transparent background. Temporarily
 * recolour the gridlines and any white axis/legend text to a dark ink, run the
 * capture, then restore the on-screen appearance. Series-coloured ticks/titles
 * are left alone — they already contrast on light.
 * @param chart - The Chart.js instance.
 * @param capture - Callback that reads the freshly-recoloured canvas.
 */
function withLightExportColors(chart: any, capture: () => void): void {
  const scales = chart.options?.scales || {};
  const keys = Object.keys(scales);
  const snaps = keys.map((k) => {
    const ax = scales[k] || {};
    return { grid: ax.grid?.color, tick: ax.ticks?.color, title: ax.title?.color };
  });
  const legend = chart.options?.plugins?.legend?.labels;
  const legendColor = legend?.color;

  keys.forEach((k) => {
    const ax = scales[k];
    if (!ax) return;
    if (ax.grid) ax.grid.color = EXPORT_LIGHT_GRID;
    if (ax.ticks && isWhiteish(ax.ticks.color)) ax.ticks.color = EXPORT_LIGHT_INK;
    if (ax.title && isWhiteish(ax.title.color)) ax.title.color = EXPORT_LIGHT_INK;
  });
  if (legend && isWhiteish(legendColor)) legend.color = EXPORT_LIGHT_INK;
  chart.update('none');
  try {
    capture();
  } finally {
    keys.forEach((k, i) => {
      const ax = scales[k];
      if (!ax) return;
      if (ax.grid) ax.grid.color = snaps[i].grid;
      if (ax.ticks) ax.ticks.color = snaps[i].tick;
      if (ax.title) ax.title.color = snaps[i].title;
    });
    if (legend) legend.color = legendColor;
    chart.update('none');
  }
}

/**
 * Composite a Chart.js canvas onto the chosen background and download it.
 * Chart.js canvases are transparent by default, so we paint a surface
 * underneath (unless the user picked a transparent PNG) and upscale the output
 * by `opts.scale` for a higher-resolution image. On a light/transparent
 * background the gridlines/white text are recoloured first so they stay visible.
 * @param chart - The Chart.js instance (the value of a react-chartjs-2 ref).
 * @param title - The chart's user-visible title; turned into a file name.
 * @param opts - The chosen background / resolution / format.
 */
function downloadChartImage(chart: any, title: string, opts: ExportOptions): void {
  if (!chart || !chart.canvas) return;
  const scale = Math.max(1, Math.min(3, opts.scale || 1));
  const fill = exportFill(opts);
  const mime = opts.format === 'jpeg' ? 'image/jpeg' : 'image/png';

  const capture = (): void => {
    const src: HTMLCanvasElement = chart.canvas;
    const out = document.createElement('canvas');
    out.width = Math.round(src.width * scale);
    out.height = Math.round(src.height * scale);
    const ctx = out.getContext('2d');
    if (!ctx) return;
    if (fill) {
      ctx.fillStyle = fill;
      ctx.fillRect(0, 0, out.width, out.height);
    }
    ctx.imageSmoothingEnabled = true;
    ctx.imageSmoothingQuality = 'high';
    ctx.drawImage(src, 0, 0, out.width, out.height);
    out.toBlob(
      (blob) => {
        if (blob) triggerDownload(blob, title, opts.format);
      },
      mime,
      opts.format === 'jpeg' ? 0.92 : undefined
    );
  };

  if (opts.background === 'light' || opts.background === 'transparent') {
    withLightExportColors(chart, capture);
  } else {
    capture();
  }
}

/**
 * Rasterise an inline SVG element into an image and download it. Used by the
 * custom-drawn box plot which is not a Chart.js chart. Because SVG is vector,
 * `opts.scale` yields a genuinely higher-resolution image.
 * @param svg - The SVG element to export.
 * @param title - The chart's user-visible title; turned into a file name.
 * @param opts - The chosen background / resolution / format.
 */
function downloadSvgImage(svg: SVGSVGElement | null, title: string, opts: ExportOptions): void {
  if (!svg) return;
  const serializer = new XMLSerializer();
  const svgString = serializer.serializeToString(svg);
  const svgBlob = new Blob([svgString], { type: 'image/svg+xml;charset=utf-8' });
  const svgUrl = URL.createObjectURL(svgBlob);
  const img = new Image();
  img.onload = (): void => {
    const vb = svg.viewBox.baseVal;
    const w = (vb && vb.width) || svg.clientWidth || 800;
    const h = (vb && vb.height) || svg.clientHeight || 400;
    const scale = Math.max(1, Math.min(3, opts.scale || 1));
    const dpr = Math.min(2, window.devicePixelRatio || 1) * scale;
    const canvas = document.createElement('canvas');
    canvas.width = Math.round(w * dpr);
    canvas.height = Math.round(h * dpr);
    const ctx = canvas.getContext('2d');
    if (!ctx) {
      URL.revokeObjectURL(svgUrl);
      return;
    }
    ctx.scale(dpr, dpr);
    const fill = exportFill(opts);
    if (fill) {
      ctx.fillStyle = fill;
      ctx.fillRect(0, 0, w, h);
    }
    ctx.drawImage(img, 0, 0, w, h);
    const mime = opts.format === 'jpeg' ? 'image/jpeg' : 'image/png';
    canvas.toBlob(
      (blob) => {
        URL.revokeObjectURL(svgUrl);
        if (blob) triggerDownload(blob, title, opts.format);
      },
      mime,
      opts.format === 'jpeg' ? 0.92 : undefined
    );
  };
  img.onerror = (): void => URL.revokeObjectURL(svgUrl);
  img.src = svgUrl;
}

const EXPORT_BG_OPTIONS: { key: ExportOptions['background']; label: string }[] = [
  { key: 'dark', label: 'Dark' },
  { key: 'light', label: 'Light' },
  { key: 'transparent', label: 'None' }
];
const EXPORT_SCALES = [1, 2, 3];
const EXPORT_FORMATS: ExportOptions['format'][] = ['png', 'jpeg'];

/**
 * Download icon that opens a small popover to choose the export appearance
 * (background, resolution, format) before saving the chart image.
 * @param props - Props.
 * @param props.onExport - Called with the chosen options when the user clicks Download.
 * @returns The button + popover.
 */
function ChartExportButton({
  onExport
}: {
  onExport: (opts: ExportOptions) => void;
}): ReactElement {
  const [open, setOpen] = useState(false);
  const [opts, setOpts] = useState<ExportOptions>(DEFAULT_EXPORT);

  const seg = (active: boolean): string =>
    `rounded-md px-2 py-1 text-xs transition ${
      active
        ? 'bg-primary-500/20 text-primary-200 ring-1 ring-primary-500/40'
        : 'text-white/60 hover:bg-gray-800 hover:text-white'
    }`;

  return (
    <div className="relative">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        title="Download chart"
        className="rounded-md p-1.5 text-white/60 transition hover:bg-gray-800 hover:text-white"
      >
        <Download className="h-4 w-4" />
      </button>
      {open && (
        <>
          {/* Click-away backdrop. */}
          <button
            type="button"
            aria-label="Close export options"
            className="fixed inset-0 z-40 cursor-default"
            onClick={() => setOpen(false)}
          />
          <div className="absolute right-0 z-50 mt-1 w-52 rounded-lg border border-gray-700/70 bg-gray-900/95 p-3 shadow-xl backdrop-blur">
            <div className="mb-2">
              <div className="mb-1 text-[11px] uppercase tracking-wide text-white/50">
                Background
              </div>
              <div className="flex gap-1">
                {EXPORT_BG_OPTIONS.map((b) => (
                  <button
                    key={b.key}
                    type="button"
                    className={seg(opts.background === b.key)}
                    onClick={() => setOpts((o) => ({ ...o, background: b.key }))}
                  >
                    {b.label}
                  </button>
                ))}
              </div>
            </div>
            <div className="mb-2">
              <div className="mb-1 text-[11px] uppercase tracking-wide text-white/50">
                Resolution
              </div>
              <div className="flex gap-1">
                {EXPORT_SCALES.map((s) => (
                  <button
                    key={s}
                    type="button"
                    className={seg(opts.scale === s)}
                    onClick={() => setOpts((o) => ({ ...o, scale: s }))}
                  >
                    {s}×
                  </button>
                ))}
              </div>
            </div>
            <div className="mb-3">
              <div className="mb-1 text-[11px] uppercase tracking-wide text-white/50">Format</div>
              <div className="flex gap-1">
                {EXPORT_FORMATS.map((f) => (
                  <button
                    key={f}
                    type="button"
                    className={seg(opts.format === f)}
                    onClick={() => setOpts((o) => ({ ...o, format: f }))}
                  >
                    {f.toUpperCase()}
                  </button>
                ))}
              </div>
            </div>
            <button
              type="button"
              onClick={() => {
                onExport(opts);
                setOpen(false);
              }}
              className="w-full rounded-md bg-gradient-to-br from-primary-500 to-pink px-3 py-1.5 text-sm font-medium text-white transition hover:brightness-110"
            >
              Download
            </button>
          </div>
        </>
      )}
    </div>
  );
}

/**
 * Chart.js legend `onClick` that toggles a series' visibility (like the
 * default) and additionally hides the left/right Y axis once it has no visible
 * series left. Lets users declutter a dual-axis chart by removing a curve and
 * its now-empty axis.
 * @param _e - The click event (unused).
 * @param legendItem - The clicked legend item.
 * @param legend - The legend plugin instance.
 */
function legendToggleOnClick(_e: any, legendItem: any, legend: any): void {
  const ci = legend.chart;
  const index = legendItem.datasetIndex;
  ci.setDatasetVisibility(index, !ci.isDatasetVisible(index));
  const axisHasVisible = (axisId: string): boolean =>
    ci.data.datasets.some(
      (ds: any, i: number) => (ds.yAxisID || 'y') === axisId && ci.isDatasetVisible(i)
    );
  if (ci.options.scales?.y) ci.options.scales.y.display = axisHasVisible('y');
  if (ci.options.scales?.y1) ci.options.scales.y1.display = axisHasVisible('y1');
  ci.update();
}

const TONE_BG: Record<string, string> = {
  info: 'from-primary-500/10 to-primary-500/5 border-primary-500/30 text-primary-300',
  warn: 'from-warning-500/15 to-warning-500/5 border-warning-500/30 text-warning-300',
  good: 'from-success-500/15 to-success-500/5 border-success-500/30 text-success-300'
};

const TONE_ICON: Record<string, ReactElement> = {
  info: <Info className="h-4 w-4" />,
  warn: <AlertTriangle className="h-4 w-4" />,
  good: <CheckCircle2 className="h-4 w-4" />
};

function pickTrendColor(direction: 'up' | 'down' | 'flat' | undefined): string {
  if (direction === 'up') return 'text-error-300';
  if (direction === 'down') return 'text-success-300';
  return 'text-white/75';
}

function pickTrendIcon(direction: 'up' | 'down' | 'flat' | undefined): typeof TrendingUp {
  if (direction === 'up') return TrendingUp;
  if (direction === 'down') return TrendingDown;
  return Minus;
}

function MetricCardView({ card }: { card: MetricCard }): ReactElement {
  const tone = card.tone || 'neutral';
  const toneRing: Record<string, string> = {
    neutral: 'border-gray-700/60',
    warn: 'border-warning-500/40',
    good: 'border-success-500/40',
    bad: 'border-error-500/40'
  };
  const dir = card.trend?.direction;
  const trendColor = pickTrendColor(dir);
  const TrendIcon = pickTrendIcon(dir);
  return (
    <div
      className={`relative overflow-hidden rounded-xl border ${toneRing[tone]} bg-gradient-to-br from-gray-900/80 to-gray-900/40 px-3 py-3 backdrop-blur`}
    >
      <div
        className="absolute -top-8 -right-8 h-20 w-20 rounded-full opacity-30 blur-2xl"
        style={{ background: 'radial-gradient(circle, #6366f1 0%, transparent 70%)' }}
      />
      <div className="text-xs uppercase tracking-wide text-white/75">{card.label}</div>
      <div className="mt-1 flex items-baseline gap-1.5">
        <div className="text-3xl font-semibold text-white tabular-nums">{card.value}</div>
        {card.unit && <div className="text-sm text-white/75">{card.unit}</div>}
      </div>
      {card.trend && (
        <div className={`mt-1.5 flex items-center gap-1 text-sm ${trendColor}`}>
          <TrendIcon className="h-3 w-3" />
          <span className="tabular-nums">
            {card.trend.pct != null ? `${card.trend.pct}%` : ''}
          </span>
          {card.trend.label && <span className="text-white/60 ml-1">{card.trend.label}</span>}
        </div>
      )}
    </div>
  );
}

function MetricsBlock({
  block
}: {
  block: Extract<RenderBlock, { kind: 'metrics' }>;
}): ReactElement {
  return (
    <div className="my-3">
      {block.title && (
        <div className="text-xs uppercase tracking-wider text-white/75 mb-2">{block.title}</div>
      )}
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
        {block.cards.map((c, i) => (
          // eslint-disable-next-line react/no-array-index-key
          <MetricCardView key={`${c.label}-${i}`} card={c} />
        ))}
      </div>
    </div>
  );
}

function LineChartBlock({
  block
}: {
  block: Extract<RenderBlock, { kind: 'line-chart' }>;
}): ReactElement {
  const settings = useChartSettings();
  const hasRightAxis = block.series.some((s) => s.axis === 'right');
  const chartRef = useRef<any>(null);
  const pinned = usePinnedTooltips({
    chartRef,
    resetKey: block,
    fontSize: settings.fontSize
  });

  const data = useMemo(
    () => ({
      datasets: block.series.map((s) => {
        const c = settings.seriesColor || s.color || '#6366F1';
        return {
          label: s.unit && !block.normalized ? `${s.name} (${s.unit})` : s.name,
          data: s.points.map(([x, y]) => ({ x, y })),
          borderColor: c,
          backgroundColor: (ctx: any) => {
            const { chart } = ctx;
            const { ctx: cc, chartArea } = chart;
            if (!chartArea) return 'rgba(99,102,241,0.15)';
            const grad = cc.createLinearGradient(0, chartArea.top, 0, chartArea.bottom);
            grad.addColorStop(0, `${c}55`);
            grad.addColorStop(1, `${c}00`);
            return grad;
          },
          borderWidth: 2,
          // Fill for area charts, or single-series line charts. Multi-series
          // line charts stay unfilled (overlapping fills look muddy).
          fill: block.area || block.series.length === 1,
          tension: 0.35,
          pointRadius: 0,
          pointHoverRadius: 4,
          yAxisID: s.axis === 'right' ? 'y1' : 'y',
          // Stash original-units recovery info for the tooltip callback.
          agentMeta: { unit: s.unit, scale: s.scale, offset: s.offset }
        };
      })
    }),
    [block, settings.seriesColor]
  );

  // How much wall-clock time the chart covers. A window spanning several days
  // (e.g. "July 7 to July 14") needs dated ticks — time-only labels would
  // repeat "6AM, 1PM, 6PM…" with no way to tell one day from the next.
  const spanMs = useMemo(() => {
    const xs = block.series.flatMap((s) => s.points.map(([x]) => x));
    if (xs.length < 2) return 0;
    return Math.max(...xs) - Math.min(...xs);
  }, [block.series]);

  const options = useMemo(() => {
    const tooltipLabel = (ctx: any): string => {
      const meta = ctx?.dataset?.agentMeta || {};
      let raw = ctx?.parsed?.y;
      if (block.normalized && typeof meta.scale === 'number' && typeof meta.offset === 'number') {
        raw = ctx.parsed.y * meta.scale + meta.offset;
      }
      const num = Number.isFinite(raw) ? Math.round(raw * 100) / 100 : raw;
      const unit = meta.unit ? ` ${meta.unit}` : '';
      return `${ctx.dataset.label}: ${num}${unit}`;
    };

    // Which series belong to each axis (used only for axis ↔ curve colouring
    // below). Axis titles are intentionally omitted: the legend and header
    // already name each series and its units, so titling the axes just repeats
    // that text in the chart corners.
    const leftSeries = block.series.filter((s) => s.axis !== 'right');
    const rightSeries = block.series.filter((s) => s.axis === 'right');

    const yLeft = {
      type: 'linear' as const,
      position: 'left' as const,
      // Ticks show the bare number; units live in the legend/header, not here.
      ticks: {
        color: '#6B7280',
        callback: (v: any) => `${v}`
      },
      grid: { color: 'rgba(75,85,99,0.15)' }
    };

    const yRight = hasRightAxis
      ? {
          type: 'linear' as const,
          position: 'right' as const,
          ticks: {
            color: '#6B7280',
            callback: (v: any) => `${v}`
          },
          grid: { drawOnChartArea: false }
        }
      : undefined;

    const built = withSettings(
      {
        responsive: true,
        maintainAspectRatio: false,
        interaction: { mode: 'index' as const, intersect: false },
        plugins: {
          legend: {
            display: block.series.length > 1,
            labels: { boxWidth: 8 },
            onClick: legendToggleOnClick
          },
          tooltip: {
            backgroundColor: 'rgba(17, 24, 39, 0.95)',
            borderColor: 'rgba(99,102,241,0.4)',
            borderWidth: 1,
            titleColor: '#E5E7EB',
            bodyColor: '#E5E7EB',
            callbacks: { label: tooltipLabel }
          }
        },
        scales: {
          x: {
            type: 'time' as const,
            time: {
              tooltipFormat: 'MMM D, HH:mm',
              // Multi-day windows tick by date; a single day ticks by clock time.
              ...(spanMs > 36 * 3600 * 1000
                ? {
                    unit: 'day' as const,
                    displayFormats: { day: 'MMM D' }
                  }
                : { displayFormats: { hour: 'HH:mm', minute: 'HH:mm' } })
            },
            ticks: { maxTicksLimit: 6 }
          },
          y: yLeft,
          ...(yRight ? { y1: yRight } : {})
        }
      },
      settings
    );

    // Colour-match each axis to its curve so the eye can pair axis ↔ line.
    // Skipped when the user has forced one global series colour (both axes
    // would then be identical) or when an axis carries mixed colours.
    if (!settings.seriesColor) {
      const axisColor = (members: typeof block.series): string | undefined => {
        const colors = Array.from(new Set(members.map((s) => s.color || '#6366F1')));
        return colors.length === 1 ? colors[0] : undefined;
      };
      const lc = axisColor(leftSeries);
      if (lc && built.scales.y) {
        built.scales.y.ticks.color = lc;
        if (built.scales.y.title) built.scales.y.title.color = lc;
      }
      const rc = axisColor(rightSeries);
      if (rc && built.scales.y1) {
        built.scales.y1.ticks.color = rc;
        if (built.scales.y1.title) built.scales.y1.title.color = rc;
      }
    }
    // Clicking a point freezes its tooltip onto the chart (click again to
    // remove it), so several points can be read side by side.
    return withPinnedTooltips(built, pinned);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [block, hasRightAxis, settings, spanMs, pinned.onChartClick, pinned.pluginOptions]);

  let headerUnit: string | undefined;
  if (block.normalized) {
    headerUnit = 'normalized 0–100';
  } else if (hasRightAxis) {
    headerUnit = `${block.unit || ''} · ${block.rightUnit || ''}`;
  } else {
    headerUnit = block.unit;
  }

  return (
    <div className="my-3 rounded-xl border border-gray-700/60 bg-gradient-to-br from-gray-900/80 to-gray-900/80 p-4">
      <div className="flex items-center justify-between mb-2">
        <div>
          <div className="text-base font-semibold text-white">{block.title}</div>
          {block.subtitle && <div className="text-sm text-white/75 mt-0.5">{block.subtitle}</div>}
        </div>
        <div className="flex items-center gap-2">
          {headerUnit && <div className="text-sm text-white/60">in {headerUnit}</div>}
          <ClearPinsButton
            count={pinned.pins.length}
            onClear={pinned.clear}
            className="flex items-center gap-1 rounded-md border border-gray-700/60 px-2 py-1 text-xs text-white/70 transition hover:bg-gray-800/60 hover:text-white"
          />
          <ChartExportButton
            onExport={(opts) => downloadChartImage(chartRef.current, block.title, opts)}
          />
        </div>
      </div>
      <div className="h-72">
        <Line
          ref={chartRef}
          data={data}
          options={options as any}
          plugins={[pinnedTooltipsPlugin]}
        />
      </div>
    </div>
  );
}

function BarChartBlock({
  block
}: {
  block: Extract<RenderBlock, { kind: 'bar-chart' }>;
}): ReactElement {
  const settings = useChartSettings();
  const data = useMemo(
    () => ({
      labels: block.categories,
      datasets: block.series.map((s) => {
        const c = settings.seriesColor || s.color || '#6366F1';
        // Per-bar colours (e.g. "your sensors" vs external stations). A global
        // seriesColor override still wins so restyle requests keep working.
        const perBar =
          !settings.seriesColor && !!s.colors && s.colors.length === s.values.length;
        const bar = s.colors as string[];
        return {
          label: s.name,
          data: s.values,
          backgroundColor: perBar ? bar.map((col) => `${col}99`) : `${c}99`,
          borderColor: perBar ? bar : c,
          borderWidth: 1,
          borderRadius: 6,
          agentMeta: { unit: s.unit, scale: s.scale, offset: s.offset }
        };
      })
    }),
    [block, settings.seriesColor]
  );

  // Vertical = columns (category on X, value on Y); horizontal = the default
  // (category on Y, value on X) which suits long device-name labels.
  const vertical = block.orientation === 'vertical';

  const options = useMemo(() => {
    const tooltipLabel = (ctx: any): string => {
      const meta = ctx?.dataset?.agentMeta || {};
      let raw = vertical ? ctx?.parsed?.y : ctx?.parsed?.x;
      if (block.normalized && typeof meta.scale === 'number' && typeof meta.offset === 'number') {
        raw = raw * meta.scale + meta.offset;
      }
      const num = Number.isFinite(raw) ? Math.round(raw * 100) / 100 : raw;
      const unit = meta.unit ? ` ${meta.unit}` : '';
      return `${ctx.dataset.label}: ${num}${unit}`;
    };

    const valueTick = (v: any): string => {
      if (block.normalized) return `${v}`;
      return block.unit ? `${v} ${block.unit}` : v;
    };

    const valueScale = {
      ticks: { color: '#6B7280', callback: valueTick },
      grid: { color: 'rgba(75,85,99,0.15)' },
      title: block.normalized
        ? { display: true, text: 'normalized 0–100', color: '#6B7280', font: { size: 10 } }
        : undefined
    };
    const categoryScale = {
      ticks: { color: '#9CA3AF' },
      grid: { display: false }
    };

    return withSettings(
      {
        responsive: true,
        maintainAspectRatio: false,
        indexAxis: (vertical ? 'x' : 'y') as 'x' | 'y',
        plugins: {
          legend: {
            display: block.series.length > 1,
            labels: { boxWidth: 8 },
            onClick: legendToggleOnClick
          },
          tooltip: {
            backgroundColor: 'rgba(17, 24, 39, 0.95)',
            borderColor: 'rgba(99,102,241,0.4)',
            borderWidth: 1,
            titleColor: '#E5E7EB',
            bodyColor: '#E5E7EB',
            callbacks: { label: tooltipLabel }
          }
        },
        scales: vertical
          ? { x: categoryScale, y: valueScale }
          : { x: valueScale, y: categoryScale }
      },
      settings
    );
  }, [block, vertical, settings]);

  let headerUnit: string | undefined;
  if (block.normalized) {
    headerUnit = 'normalized 0–100';
  } else {
    headerUnit = block.unit;
  }

  // Horizontal: height scales with category count. Vertical: fixed-ish.
  const perRow = 22 + block.series.length * 16;
  const chartHeight = vertical
    ? 360
    : Math.max(220, block.categories.length * perRow);

  const chartRef = useRef<any>(null);
  return (
    <div className="my-3 rounded-xl border border-gray-700/60 bg-gradient-to-br from-gray-900/80 to-gray-900/80 p-4">
      <div className="flex items-center justify-between mb-2">
        <div>
          <div className="text-base font-semibold text-white">{block.title}</div>
          {block.subtitle && <div className="text-sm text-white/75 mt-0.5">{block.subtitle}</div>}
        </div>
        <div className="flex items-center gap-2">
          {headerUnit && <div className="text-sm text-white/60">in {headerUnit}</div>}
          <ChartExportButton
            onExport={(opts) => downloadChartImage(chartRef.current, block.title, opts)}
          />
        </div>
      </div>
      <div style={{ height: chartHeight }}>
        <Bar ref={chartRef} data={data} options={options as any} />
      </div>
    </div>
  );
}

function CalloutBlock({
  block
}: {
  block: Extract<RenderBlock, { kind: 'callout' }>;
}): ReactElement {
  return (
    <div
      className={`my-3 rounded-xl border bg-gradient-to-br p-3 ${TONE_BG[block.tone] || TONE_BG.info}`}
    >
      <div className="flex items-center gap-2 text-sm font-semibold uppercase tracking-wide">
        {TONE_ICON[block.tone] || TONE_ICON.info}
        {block.title}
      </div>
      <div className="mt-1.5 text-base text-white/85">{block.body}</div>
    </div>
  );
}

const CHART_PALETTE = [
  '#6366F1',
  '#06B6D4',
  '#F59E0B',
  '#EC4899',
  '#10B981',
  '#8B5CF6',
  '#EF4444',
  '#3B82F6'
];

const GAUGE_TONE_COLOR: Record<string, string> = {
  good: '#10B981',
  warn: '#F59E0B',
  bad: '#EF4444'
};

function gaugeColorFor(
  value: number,
  zones?: { upTo: number; tone: 'good' | 'warn' | 'bad' }[]
): string {
  if (!zones || !zones.length) return '#6366F1';
  const hit = zones.find((z) => value <= z.upTo);
  return hit ? GAUGE_TONE_COLOR[hit.tone] : GAUGE_TONE_COLOR.bad;
}

function ScatterChartBlock({
  block
}: {
  block: Extract<RenderBlock, { kind: 'scatter-chart' }>;
}): ReactElement {
  const settings = useChartSettings();
  const color = settings.seriesColor || block.color || '#6366F1';
  const data = useMemo(
    () => ({
      datasets: [
        {
          label: `${block.yLabel} vs ${block.xLabel}`,
          data: block.points,
          backgroundColor: `${color}99`,
          borderColor: color,
          pointRadius: 3,
          pointHoverRadius: 5
        }
      ]
    }),
    [block, color]
  );

  const options = useMemo(
    () =>
      withSettings(
        {
          responsive: true,
          maintainAspectRatio: false,
          plugins: {
            legend: { display: false },
            tooltip: {
              backgroundColor: 'rgba(17, 24, 39, 0.95)',
              borderColor: 'rgba(99,102,241,0.4)',
              borderWidth: 1,
              titleColor: '#E5E7EB',
              bodyColor: '#E5E7EB',
              callbacks: {
                label: (ctx: any): string => {
                  const x = Math.round(ctx.parsed.x * 100) / 100;
                  const y = Math.round(ctx.parsed.y * 100) / 100;
                  const xu = block.xUnit ? ` ${block.xUnit}` : '';
                  const yu = block.yUnit ? ` ${block.yUnit}` : '';
                  return `${block.xLabel} ${x}${xu} · ${block.yLabel} ${y}${yu}`;
                }
              }
            }
          },
          scales: {
            x: {
              type: 'linear' as const,
              title: {
                display: true,
                text: block.xUnit ? `${block.xLabel} (${block.xUnit})` : block.xLabel
              }
            },
            y: {
              type: 'linear' as const,
              title: {
                display: true,
                text: block.yUnit ? `${block.yLabel} (${block.yUnit})` : block.yLabel
              }
            }
          }
        },
        settings
      ),
    [block, settings]
  );

  const chartRef = useRef<any>(null);
  return (
    <div className="my-3 rounded-xl border border-gray-700/60 bg-gradient-to-br from-gray-900/80 to-gray-900/80 p-4">
      <div className="mb-2 flex items-start justify-between gap-2">
        <div>
          <div className="text-base font-semibold text-white">{block.title}</div>
          {block.subtitle && <div className="mt-0.5 text-sm text-white/75">{block.subtitle}</div>}
        </div>
        <ChartExportButton
          onExport={(opts) => downloadChartImage(chartRef.current, block.title, opts)}
        />
      </div>
      <div className="h-80">
        <Scatter ref={chartRef} data={data} options={options as any} />
      </div>
    </div>
  );
}

function DonutChartBlock({
  block
}: {
  block: Extract<RenderBlock, { kind: 'donut-chart' }>;
}): ReactElement {
  const settings = useChartSettings();
  const total = block.segments.reduce((a, s) => a + (s.value || 0), 0) || 1;
  const data = useMemo(
    () => ({
      labels: block.segments.map((s) => s.label),
      datasets: [
        {
          data: block.segments.map((s) => s.value),
          backgroundColor: block.segments.map(
            (s, i) => s.color || CHART_PALETTE[i % CHART_PALETTE.length]
          ),
          borderColor: 'rgba(17,24,39,0.9)',
          borderWidth: 2
        }
      ]
    }),
    [block]
  );

  const options = useMemo(
    () =>
      withSettings(
        {
          responsive: true,
          maintainAspectRatio: false,
          cutout: '62%',
          plugins: {
            legend: {
              position: 'right' as const,
              labels: { boxWidth: 12 }
            },
            tooltip: {
              backgroundColor: 'rgba(17, 24, 39, 0.95)',
              borderColor: 'rgba(99,102,241,0.4)',
              borderWidth: 1,
              titleColor: '#E5E7EB',
              bodyColor: '#E5E7EB',
              callbacks: {
                label: (ctx: any): string => {
                  const v = Math.round(ctx.parsed * 100) / 100;
                  const pct = Math.round((ctx.parsed / total) * 1000) / 10;
                  const unit = block.unit ? ` ${block.unit}` : '';
                  return `${ctx.label}: ${v}${unit} (${pct}%)`;
                }
              }
            }
          }
        },
        settings
      ),
    [block, total, settings]
  );

  const chartRef = useRef<any>(null);
  return (
    <div className="my-3 rounded-xl border border-gray-700/60 bg-gradient-to-br from-gray-900/80 to-gray-900/80 p-4">
      <div className="mb-2 flex items-start justify-between gap-2">
        <div>
          <div className="text-base font-semibold text-white">{block.title}</div>
          {block.subtitle && <div className="mt-0.5 text-sm text-white/75">{block.subtitle}</div>}
        </div>
        <ChartExportButton
          onExport={(opts) => downloadChartImage(chartRef.current, block.title, opts)}
        />
      </div>
      <div className="h-72">
        <Doughnut ref={chartRef} data={data} options={options as any} />
      </div>
    </div>
  );
}

function GaugeBlock({
  block
}: {
  block: Extract<RenderBlock, { kind: 'gauge' }>;
}): ReactElement {
  const span = block.max - block.min || 1;
  const clamped = Math.min(block.max, Math.max(block.min, block.value));
  const frac = (clamped - block.min) / span;
  const color = gaugeColorFor(block.value, block.zones);

  const data = useMemo(
    () => ({
      datasets: [
        {
          data: [frac, 1 - frac],
          backgroundColor: [color, 'rgba(75,85,99,0.22)'],
          borderWidth: 0
        }
      ]
    }),
    [frac, color]
  );

  const options = useMemo(
    () => ({
      responsive: true,
      maintainAspectRatio: false,
      cutout: '76%',
      plugins: { legend: { display: false }, tooltip: { enabled: false } }
    }),
    []
  );

  const display = Math.round(block.value * 100) / 100;
  const chartRef = useRef<any>(null);

  return (
    <div className="my-3 rounded-xl border border-gray-700/60 bg-gradient-to-br from-gray-900/80 to-gray-900/80 p-4">
      <div className="mb-1 flex items-start justify-between gap-2">
        <div>
          <div className="text-base font-semibold text-white">{block.title}</div>
          {block.subtitle && <div className="mt-0.5 text-sm text-white/75">{block.subtitle}</div>}
        </div>
        <ChartExportButton
          onExport={(opts) => downloadChartImage(chartRef.current, block.title, opts)}
        />
      </div>
      <div className="flex items-center gap-4">
        <div className="relative h-40 w-40 shrink-0">
          <Doughnut ref={chartRef} data={data} options={options as any} />
          <div className="absolute inset-0 flex flex-col items-center justify-center">
            <div className="text-3xl font-semibold tabular-nums text-white">{display}</div>
            {block.unit && <div className="text-xs text-white/75">{block.unit}</div>}
          </div>
        </div>
        <div className="space-y-1.5 text-sm text-white/75">
          <div>
            Range <span className="tabular-nums text-white/90">{block.min}</span> –{' '}
            <span className="tabular-nums text-white/90">{block.max}</span>
            {block.unit ? ` ${block.unit}` : ''}
          </div>
          <div className="flex items-center gap-1.5">
            <span
              className="inline-block h-2.5 w-2.5 rounded-full"
              style={{ backgroundColor: color }}
            />
            <span>{Math.round(frac * 100)}% of range</span>
          </div>
        </div>
      </div>
    </div>
  );
}

const HEALTH_TONE_COLOR: Record<string, string> = {
  good: '#10B981',
  warn: '#F59E0B',
  bad: '#EF4444'
};

const ACTION_PRIORITY_COLOR: Record<string, string> = {
  high: '#EF4444',
  med: '#F59E0B',
  low: '#1990FF'
};
const ACTION_PRIORITY_LABEL: Record<string, string> = {
  high: 'High',
  med: 'Medium',
  low: 'Low'
};

function BoxPlotBlock({
  block
}: {
  block: Extract<RenderBlock, { kind: 'box-plot' }>;
}): ReactElement | null {
  const settings = useChartSettings();
  const stats = block.stats || [];
  if (!stats.length) return null;
  const color = settings.seriesColor || block.color || '#6366F1';
  const allMin = Math.min(...stats.map((s) => s.min));
  const allMax = Math.max(...stats.map((s) => s.max));
  const span = allMax - allMin || 1;
  const rowH = Math.max(28, settings.fontSize * 2.4);
  const margin = { left: 140, right: 24, top: 16, bottom: 36 };
  const innerWidth = 520;
  const innerHeight = stats.length * rowH;
  const totalWidth = margin.left + innerWidth + margin.right;
  const totalHeight = margin.top + innerHeight + margin.bottom;
  const xScale = (v: number): number =>
    margin.left + ((v - allMin) / span) * innerWidth;
  const ticks = [0, 0.5, 1].map((t) => allMin + t * span);
  const gridStroke = settings.showGrid
    ? hexToRgba(settings.gridColor, 0.4)
    : 'transparent';

  const svgRef = useRef<SVGSVGElement>(null);
  return (
    <div className="my-3 rounded-xl border border-gray-700/60 bg-gradient-to-br from-gray-900/80 to-gray-900/80 p-4">
      <div className="mb-2 flex items-start justify-between gap-2">
        <div>
          <div className="text-base font-semibold text-white">{block.title}</div>
          {block.subtitle && (
            <div className="mt-0.5 text-sm text-white/75">{block.subtitle}</div>
          )}
        </div>
        <ChartExportButton
          onExport={(opts) => downloadSvgImage(svgRef.current, block.title, opts)}
        />
      </div>
      <svg
        ref={svgRef}
        viewBox={`0 0 ${totalWidth} ${totalHeight}`}
        className="w-full"
        preserveAspectRatio="xMidYMid meet"
      >
        <line
          x1={margin.left}
          x2={margin.left + innerWidth}
          y1={margin.top + innerHeight}
          y2={margin.top + innerHeight}
          stroke={gridStroke}
        />
        {ticks.map((t, i) => (
          // eslint-disable-next-line react/no-array-index-key
          <g key={i}>
            <line
              x1={xScale(t)}
              x2={xScale(t)}
              y1={margin.top + innerHeight}
              y2={margin.top + innerHeight + 4}
              stroke={gridStroke}
            />
            <text
              x={xScale(t)}
              y={margin.top + innerHeight + 16 + Math.max(0, settings.fontSize - 12)}
              // Edge ticks sit flush against the plot bounds; centering them
              // lets the label overflow the SVG and clip. Anchor the first
              // label to its start and the last to its end so both stay inside.
              // eslint-disable-next-line no-nested-ternary
              textAnchor={i === 0 ? 'start' : i === ticks.length - 1 ? 'end' : 'middle'}
              fontSize={settings.fontSize}
              fill={settings.tickColor}
            >
              {`${Math.round(t * 100) / 100}${block.unit ? ` ${block.unit}` : ''}`}
            </text>
          </g>
        ))}
        {stats.map((s, i) => {
          const y = margin.top + i * rowH + rowH / 2;
          const xMin = xScale(s.min);
          const xQ1 = xScale(s.q1);
          const xMed = xScale(s.median);
          const xQ3 = xScale(s.q3);
          const xMax = xScale(s.max);
          const boxTop = y - 9;
          const boxBot = y + 9;
          return (
            // eslint-disable-next-line react/no-array-index-key
            <g key={i}>
              <text
                x={margin.left - 8}
                y={y + 4}
                textAnchor="end"
                fontSize={settings.fontSize}
                fill={settings.tickColor}
              >
                {s.category}
              </text>
              <line x1={xMin} x2={xMax} y1={y} y2={y} stroke={color} strokeWidth={1} />
              <line x1={xMin} x2={xMin} y1={y - 4} y2={y + 4} stroke={color} strokeWidth={1} />
              <line x1={xMax} x2={xMax} y1={y - 4} y2={y + 4} stroke={color} strokeWidth={1} />
              <rect
                x={xQ1}
                y={boxTop}
                width={Math.max(1, xQ3 - xQ1)}
                height={boxBot - boxTop}
                fill={`${color}55`}
                stroke={color}
                strokeWidth={1}
                rx={2}
              />
              <line
                x1={xMed}
                x2={xMed}
                y1={boxTop}
                y2={boxBot}
                stroke="#fff"
                strokeWidth={1.5}
              />
              {s.mean !== undefined && (
                <circle cx={xScale(s.mean)} cy={y} r={2} fill="#fff" />
              )}
            </g>
          );
        })}
      </svg>
    </div>
  );
}

function HealthImpactBlock({
  block
}: {
  block: Extract<RenderBlock, { kind: 'health-impact' }>;
}): ReactElement {
  return (
    <div className="my-3 rounded-xl border border-gray-700/60 bg-gradient-to-br from-gray-900/80 to-gray-900/80 p-4">
      <div className="mb-3">
        <div className="text-sm font-semibold text-white">
          {block.title || 'Human impact'}
        </div>
        {block.subtitle && (
          <div className="mt-0.5 text-xs text-white/75">{block.subtitle}</div>
        )}
      </div>
      <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
        {block.scores.map((s, i) => {
          const color = HEALTH_TONE_COLOR[s.tone] || '#6366F1';
          const barPct = Math.min(100, Math.abs(s.impactPct));
          return (
            <div
              // eslint-disable-next-line react/no-array-index-key
              key={i}
              className="rounded-lg border border-gray-700/60 bg-gray-900/60 px-3 py-2.5"
            >
              <div className="flex items-baseline justify-between gap-2">
                <div className="text-[11px] uppercase tracking-wide text-white/75">
                  {s.label}
                </div>
                <div
                  className="text-sm font-semibold tabular-nums"
                  style={{ color }}
                >
                  {s.impactPct >= 0 ? '+' : ''}
                  {s.impactPct}%
                </div>
              </div>
              <div className="mt-1.5 h-1.5 w-full overflow-hidden rounded-full bg-gray-800">
                <div
                  className="h-full rounded-full"
                  style={{ width: `${barPct}%`, backgroundColor: color }}
                />
              </div>
              <div className="mt-1 text-[11px] text-white/60">{s.basis}</div>
              <div className="mt-1 text-xs text-white/90">{s.body}</div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function ActionsBlock({
  block
}: {
  block: Extract<RenderBlock, { kind: 'actions' }>;
}): ReactElement {
  const sorted = [...block.items].sort((a, b) => {
    const rank = (p: 'high' | 'med' | 'low'): number => {
      if (p === 'high') return 0;
      if (p === 'med') return 1;
      return 2;
    };
    return rank(a.priority) - rank(b.priority);
  });
  return (
    <div className="my-3 rounded-xl border border-gray-700/60 bg-gradient-to-br from-gray-900/80 to-gray-900/80 p-4">
      <div className="mb-3 flex items-center justify-between gap-2">
        <div className="text-sm font-semibold text-white">
          {block.title || 'Next steps'}
        </div>
        {block.subtitle && (
          <div className="text-xs text-white/75">{block.subtitle}</div>
        )}
      </div>
      <ul className="space-y-2">
        {sorted.map((it, i) => {
          const color = ACTION_PRIORITY_COLOR[it.priority] || '#1990FF';
          return (
            <li
              // eslint-disable-next-line react/no-array-index-key
              key={i}
              className="flex gap-3 rounded-lg border border-gray-700/60 bg-gray-900/60 p-3"
            >
              <div
                className="mt-1 h-2.5 w-2.5 shrink-0 rounded-full"
                style={{ backgroundColor: color }}
              />
              <div className="min-w-0 flex-1">
                <div className="flex items-baseline justify-between gap-2">
                  <div className="text-sm font-medium text-white">{it.title}</div>
                  <div
                    className="text-[10px] uppercase tracking-wide"
                    style={{ color }}
                  >
                    {ACTION_PRIORITY_LABEL[it.priority] || it.priority}
                  </div>
                </div>
                <div className="mt-0.5 text-xs text-white/90">{it.body}</div>
              </div>
            </li>
          );
        })}
      </ul>
    </div>
  );
}

function formatBytes(n: number): string {
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / (1024 * 1024)).toFixed(2)} MB`;
}

function DownloadBlock({
  block
}: {
  block: Extract<RenderBlock, { kind: 'download' }>;
}): ReactElement {
  const onDownload = (): void => {
    const blob = new Blob([block.content], { type: block.mime || 'application/octet-stream' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = block.filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };
  return (
    <div className="my-3 flex items-center gap-3 rounded-xl border border-gray-700/60 bg-gradient-to-br from-gray-900/80 to-gray-900/80 p-4">
      <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-lg bg-primary-500/15 text-primary-300">
        <Download className="h-6 w-6" />
      </div>
      <div className="min-w-0 flex-1">
        <div className="text-base font-semibold text-white">{block.title}</div>
        <div className="mt-0.5 truncate text-sm text-white/75">
          {block.filename} · {formatBytes(block.byteCount)}
        </div>
        {block.description && (
          <div className="mt-1 text-sm text-white/90">{block.description}</div>
        )}
      </div>
      <button
        type="button"
        onClick={onDownload}
        className="rounded-lg border border-primary-500/40 bg-primary-500/10 px-3 py-2 text-sm font-medium text-primary-200 transition hover:border-primary-500/60 hover:bg-primary-500/15 hover:text-white"
      >
        Download
      </button>
    </div>
  );
}

/**
 * Render a single agent render-block (chart, metrics, callout, etc).
 * @param props - Props.
 * @param props.block - The block payload emitted by an agent tool.
 * @returns The matching React node, or null if the kind is unknown.
 */
function AgentBlock({ block }: { block: RenderBlock }): ReactElement | null {
  switch (block.kind) {
    case 'metrics':
      return <MetricsBlock block={block} />;
    case 'line-chart':
      return <LineChartBlock block={block} />;
    case 'bar-chart':
      return <BarChartBlock block={block} />;
    case 'scatter-chart':
      return <ScatterChartBlock block={block} />;
    case 'donut-chart':
      return <DonutChartBlock block={block} />;
    case 'gauge':
      return <GaugeBlock block={block} />;
    case 'box-plot':
      return <BoxPlotBlock block={block} />;
    case 'health-impact':
      return <HealthImpactBlock block={block} />;
    case 'actions':
      return <ActionsBlock block={block} />;
    case 'download':
      return <DownloadBlock block={block} />;
    case 'callout':
      return <CalloutBlock block={block} />;
    default:
      return null;
  }
}

export default AgentBlock;
